const Red = 0xff0000;
const Green = 0x00ff00;
const Blue = 0x0000ff;

/**
 * Converts a number into a hex color string (prefixed with #).
 * @param value The number value.
 * @param convertBgr Whether to convert from reversed BGR to RGB.
 * @returns
 */
export const toHexColor = (value: number, convertBgr = false) => {
	if (convertBgr) {
		const r = (value >> 16) & 0xff;
		const g = (value >> 8) & 0xff;
		const b = value & 0xff;

		value = (b << 16) + (g << 8) + r;
	}

	const hexColor = '#' + value.toString(16).padStart(6, '0');
	return hexColor;
};

/**
 * Returns an object with r, g, b properties with the component colors from RGB
 * @param  {Number} color The RGB color
 */
export const colorToComponents = (color: string | number) => {
	if (typeof color === 'string') {
		color = parseInt(color, 10);
	}

	const rgb = {
		r: (color & Red) >> 16,
		g: (color & Green) >> 8,
		b: color & Blue,
	};

	return rgb;
};

export const colorToLuminosity = (color: string | number) => {
	if (typeof color === 'string') {
		color = Number(color);
	}

	const b = color & Blue,
		g = (color & Green) >> 8,
		r = (color & Red) >> 16;

	return r * 0.33 + g * 0.5 + b * 0.16;
};

export const makeContrastingColor = (color: string | number, light = '#fff', dark = '#333') => {
	const luminosity = colorToLuminosity(color);
	return luminosity < 128 ? light : dark;
};

/**
 * Converts a Jade integer colour (as BGR) into a standard RGB hex colour.
 */
export const jadeColourToHex = (jadeColour: number | undefined) => {
	return toHexColor(jadeColour ?? 0, true);
};

export const hexToJadeColor = (hexColor: string | number) => {
	if (typeof hexColor === 'number') {
		hexColor = hexColor.toString(16);
	}

	if (!hexColor) {
		return 0;
	}

	if (/^#/.test(hexColor)) {
		hexColor = hexColor.substring(1);
	}

	const val = parseInt(hexColor, 16);
	const jadeColor = ((val & Blue) << 16) + (val & Green) + ((val & Red) >> 16);
	return jadeColor;
};

export const hexToColor = (hexColor: string) => {
	if (!hexColor) {
		return 0;
	}

	if (/^#/.test(hexColor)) {
		hexColor = hexColor.substring(1);
	}

	const val = parseInt(hexColor, 16);
	return val;
};

/**
 * Produces an interpolated color being the color of the proportional distance between
 * colorStart and colorEnd, with prop being the proportion along the scale.
 * @param  {Number} colorStart Start color RGB
 * @param  {Number} colorEnd End color RGB
 * @param  {Number} prop Proportion between 0 and 1
 */
export const interpolateColor = (colorStart: string | number, colorEnd: string | number, prop: number) => {
	if (prop < 0) {
		prop = 0;
	} else if (prop > 1) {
		prop = 1;
	}

	const start = colorToComponents(colorStart),
		end = colorToComponents(colorEnd),
		rStart = start.r,
		rEnd = end.r,
		gStart = start.g,
		gEnd = end.g,
		bStart = start.b,
		bEnd = end.b;

	const r = Math.floor(rStart + (rEnd - rStart) * prop),
		g = Math.floor(gStart + (gEnd - gStart) * prop),
		b = Math.floor(bStart + (bEnd - bStart) * prop),
		rgb = b + g * 256 + r * 256 * 256;

	return rgb;
};
